import { type IFixedRuleType } from '@framework/engine/schema/rules/IRuleType.ts';
import { FormBuilderMetaFlags } from '@framework/engine/validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { type IEmailVerificationWidget } from './IEmailVerificationWidget.ts';

export function emailVerificationWidgetStateFieldName(code: string) {
  return `${code}@state`;
}

export enum EmailVerificationState {
  NotVerified = 'not-verified',
  Verifying = 'verifying',
  Verified = 'verified',
}

export function createEmailVerificationWidgetFormSchema(input: {
  widget: IEmailVerificationWidget;
}) {
  const { widget } = input;

  const verificationStateName = emailVerificationWidgetStateFieldName(
    widget.code,
  );

  const stateSchema = yup
    .string()
    .default(EmailVerificationState.NotVerified)
    .oneOfEnum(EmailVerificationState)
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipValidationRules,
      FormBuilderMetaFlags.SkipDefaultValue,
    );

  return {
    [widget.code]: yup.string().notRequired().email(),
    [verificationStateName]: stateSchema,
  };
}

/**
 * Regola fissa per validare che il campo sia stato verificato.
 * Solo lato FE, lato BE è replicato.
 */
export const EmailVerificationWidgetRule: IFixedRuleType = {
  name: 'Verifica E-Mail',
  validator: (schema, widget) => {
    const emailWidget = widget as unknown as IEmailVerificationWidget;
    const verificationStateName = emailVerificationWidgetStateFieldName(
      emailWidget.code,
    );

    return schema.when(
      [verificationStateName],
      ([verificationState], schema) => {
        const applyRules = schema.meta?.()?.applyRules ?? true;
        if (!applyRules) return schema;

        if (verificationState !== EmailVerificationState.Verified) {
          return schema.test({
            name: 'email-verified',
            skipAbsent: true,
            message: t(
              'email-verification-rule.message',
              `È necessario verificare l'email per proseguire`,
            ),
            test: value => {
              // Non effettua la validazione se il campo è vuoto. In quel
              // caso la verifica è delegata al required.
              if (!value) return true;

              return false;
            },
          });
        }

        return schema;
      },
    );
  },
};
